import { isPastDate } from "@gaiaherbs/Utilities/GAIADateUtilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import logger from "@insite/client-framework/Logger";
import { getPageByUrl, getPagesByParent, PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps, { PageModel } from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageStyle } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
        [fields.title]: string;
    };
}

interface EventProps {
    currentEvents: any;
    links: any;
}

const mapStateToProps = (state: ApplicationState) => {
    const newsAndListPageUrl = getPageLinkByPageType(state, "NewsListPage")?.type as string;
    const nodeId: string = state.links.pageTypesToNodeId[newsAndListPageUrl];

    return {
        parentNodeId: nodeId,
        location: getLocation(state),
        parameter: state.pages.newsList.getNewsPagesParameters[nodeId] ?? {
            parentNodeId: nodeId,
        },
        viewAll: getPageLinkByPageType(state, "NewsListPage")?.url,
        propsState: state,
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    titleLink?: LinkPresentationProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    listWrapperContainer?: InjectableCss;
    listWrapperWithCarousal?: InjectableCss;
    listWrapperWithoutCarousal?: InjectableCss;
    listMainWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    viewAllLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    wrapper?: InjectableCss;
    title?: TypographyProps;
    header?: TextFieldPresentationProps;
    prevArrowContainer?: InjectableCss;
    prevArrowButton?: ButtonProps;
    nextArrowContainer?: InjectableCss;
    nextArrowButton?: ButtonProps;
    carousalMainContainer?: InjectableCss;
}

export const newsListViewStyles: NewsListViewStyles = {
    titleText: {
        size: 20,
        css: css`
            margin-bottom: 0;
        `,
    },
    titleLink: {
        color: "primary",
    },
    newsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            background-color: #f9f7f5;
            border-radius: 10px;
            padding: 30px;
            justify-content: space-around;
            align-items: flex-start;
            margin-right: 10px;
            position: relative;
            flex: 0 0 32.8%;

            .news-content {
                width: 100%;
                /* padding: 0.5rem 1rem 0rem; */
                h3 {
                    color: ${getColor("primary")};
                    font-size: 16px;
                    font-weight: "bold";
                    margin: 0;
                    a {
                        & :focus {
                            outline: none;
                        }
                        span {
                            color: #000;
                        }
                    }
                }

                h5 {
                    font-size: 14px;
                    /* color: #000; */
                    margin-bottom: 5px;
                    /* background: #f0f4f3; */
                    width: fit-content;
                    padding: 2px 10px;
                    border-radius: 50px;
                }

                p {
                    font-size: 14px;
                }
                .read-more {
                    color: ${getColor("common.accent")};
                    font-weight: bold;
                }
            }

            .author-img {
                width: 60px;
                height: 60px;
                > ${LazyImageStyle} {
                    border-radius: 100%;
                    width: 100%;
                }
                img {
                    height: 100%;
                }
                .img-wrapper {
                    img {
                        height: inherit;
                    }
                }
            }
            .author-name {
                color: #205c40;
                font-size: 16px;
                font-family: "LatoBold";
                margin-left: 16px;
            }
            .summary-author-info {
                display: flex;
                align-items: center;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 0 0 100%;
                    `,
                    css`
                        flex: 0 0 100%;
                    `,
                    css`
                        flex: 0 0 49.5%;
                    `,
                    css`
                        flex: 0 0 49.5%;
                    `,
                    css``,
                ])}
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
            `,
        },
    },
    summaryWrapper: {
        css: css`
            margin-top: 8px;
            p {
                margin-bottom: 8px !important;
            }
        `,
    },
    dateAndAuthorWrapper: {
        css: css`
            margin-top: 8px;
        `,
    },
    authorText: {
        css: css`
            font-weight: bold;
            margin-right: 8px;
        `,
    },
    publishDateText: {
        css: css`
            color: #183028;
            font-size: 14px;
            font-family: "LatoBold";
        `,
    },
    listWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    listWrapperContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    listWrapperWithCarousal: {
        css: css`
            display: flex;
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css``,
                    css`
                        margin-right: 7px;
                    `,
                    css`
                        margin-right: 7px;
                    `,
                ])}
        `,
    },
    listWrapperWithoutCarousal: {
        css: css`
            display: flex;
            justify-content: center;
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 0rem 1.5rem;
            margin-bottom: 30px;
        `,
    },
    title: {
        variant: "h2",
        color: "primary",
        css: css`
            // background: transparent;
            width: 100%;
            font-size: 32px;
            padding: 14px 25px;
            text-align: center;
        `,
    },
    viewAllLink: {
        css: css`
            display: flex;
            justify-content: center;
            font-size: 14px;
            font-weight: bold;
            border: 2px solid ${getColor("common.accent")};
            width: fit-content;
            height: 40px;
            border-radius: 10px;
            padding: 0px 1.5rem;
            margin: 1rem 0rem 2rem;
        `,
    },
    readMoreLink: {
        css: css`
            font-size: 14px;
            font-weight: bold;
            margin-top: 8px;
        `,
    },
    carousalMainContainer: {
        css: css`
            position: relative;
            width: 100%;
        `,
    },
    nextArrowContainer: {
        css: css`
            justify-content: flex-end;
            position: absolute;
            right: -15px;
            top: 40%;
            width: 40px;
            height: 40px;
            overflow: hidden;
            border-radius: 50%;
            border: 1px solid #e5e5e5;

            z-index: 2;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            background: #fff;
            span {
                width: 30px;
                height: 30px;
                padding-left: 3px;
                top: 0 !important;
                > svg {
                    color: #d14124;
                    width: 25px;
                    height: 25px;
                }
            }
            &:hover {
                background: #d14124;
                svg {
                    color: #fff;
                }
            }
        `,
    },
    prevArrowContainer: {
        css: css`
            justify-content: flex-start;
            position: absolute;
            left: -15px;
            top: 40%;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            border: 1px solid #e5e5e5;
            overflow: hidden;
            z-index: 2;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            background: #fff;
            span {
                width: 30px;
                height: 30px;
                top: 0 !important;
                > svg {
                    color: #d14124;
                    width: 25px;
                    height: 25px;
                }
            }
            &:hover {
                background: #d14124;
                svg {
                    color: #fff;
                }
            }
        `,
    },
};

const styles = newsListViewStyles;

type MyState = {
    pages: PageModel[] | undefined;
    links: SafeDictionary<PageLinkModel>;
    requestMade: boolean;
};

const EventCarausel = ({ currentEvents, links }: EventProps) => {
    let isCarousal = false;

    if (window.screen.width > 1200 && currentEvents.length > 3) {
        isCarousal = true;
    } else if (currentEvents.length > 2 && window.screen.width < 1200 && window.screen.width > 767) {
        isCarousal = true;
    } else if (window.screen.width < 768 && currentEvents.length > 1) {
        isCarousal = true;
    } else {
        isCarousal = false;
    }

    const [emblaRef, embla] = useEmblaCarousel();
    const [, setCanScrollPrev] = React.useState(false);
    const [, setCanScrollNext] = React.useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
        embla.reInit({
            align: "start",
            slidesToScroll: 1,
            loop: true,
        });
        setCanScroll();
    }, [embla]);

    const scrollNext = React.useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);
    // useRecursiveTimeout(scrollNext, 3000);
    return (
        <StyledWrapper {...styles.carousalMainContainer}>
            {isCarousal && (
                <StyledWrapper {...styles.prevArrowContainer}>
                    <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                        <ButtonIcon src={ChevronLeft} />
                    </Button>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.listWrapper}>
                <StyledWrapper {...styles.listWrapperContainer} ref={isCarousal ? emblaRef : null}>
                    <StyledWrapper
                        {...(isCarousal
                            ? { ...styles.listWrapperWithCarousal }
                            : { ...styles.listWrapperWithoutCarousal })}
                    >
                        {currentEvents.map((page: PageProps) => (
                            <Clickable href={links[page.nodeId]?.url} {...styles.newsWrapper} key={page.id}>
                                <Typography variant="h2" {...styles.titleText}>
                                    <Link href={links[page.nodeId]?.url} {...styles.titleLink}>
                                        {Object.values(page.translatableFields["title"])[0]}
                                    </Link>
                                </Typography>
                                <StyledWrapper {...styles.dateAndAuthorWrapper}>
                                    {Object.values(page.translatableFields["eventDisplayDateTime"])[0] && (
                                        <Typography {...styles.publishDateText}>
                                            {Object.values(page.translatableFields["eventDisplayDateTime"])[0]}
                                        </Typography>
                                    )}
                                </StyledWrapper>
                                <StyledWrapper {...styles.summaryWrapper}>
                                    {Object.values(page.translatableFields["newsSummary"])[0] &&
                                        parse(Object.values(page.translatableFields["newsSummary"])[0], parserOptions)}
                                    <StyledWrapper className="summary-author-info">
                                        <StyledWrapper className="author-img">
                                            <LazyImage src={Object.values(page.translatableFields["authorImage"])[0]} />
                                        </StyledWrapper>
                                        <StyledWrapper className="author-name">
                                            <Typography>
                                                {Object.values(page.translatableFields["author"])[0]}
                                            </Typography>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                    <Link href={links[page.nodeId]?.url} {...styles.readMoreLink}>
                                        {translate("Read More")}
                                    </Link>
                                </StyledWrapper>
                            </Clickable>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {isCarousal && (
                <StyledWrapper {...styles.nextArrowContainer}>
                    <Button {...styles.nextArrowButton} onClick={() => embla && embla.scrollNext()}>
                        <ButtonIcon src={ChevronRight} />
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

class GAIANewsAndEventsListView extends React.Component<Props> {
    state: MyState = {
        pages: [],
        links: {},
        requestMade: false,
    };

    componentDidUpdate(prevProps: any, prevState: { requestMade: boolean }) {
        if (!prevState.requestMade) {
            this.getPagesAndLinks();
        }
    }

    componentDidMount() {
        this.getPagesAndLinks();
    }

    getPagesAndLinks = async () => {
        const { propsState } = this.props;
        const links: SafeDictionary<PageLinkModel> = {};
        try {
            const pageByUrl = await getPageByUrl("/Events");
            if (pageByUrl && pageByUrl.page) {
                // this.props.loadPagesForParent({ parentNodeId: pageByUrl.page.nodeId });
                const childPages = await getPagesByParent({ parentNodeId: pageByUrl.page.nodeId });
                if (childPages.pages) {
                    const pages: PageModel[] | undefined = childPages.pages;
                    pages.forEach(page => {
                        links[page.nodeId] = getPageLinkByNodeId(propsState, page.nodeId);
                    });
                    this.setState({ pages: childPages.pages, links, requestMade: true });
                }
            }
        } catch (err) {
            logger.error(err);
        }
    };

    changePage = (newPageIndex: number) => {
        this.updateQueryString(newPageIndex, this.props.parameter.pageSize);
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        this.updateQueryString(1, newPageSize);
    };

    updateQueryString(page: number, pageSize?: number) {
        const {
            history,
            location: { pathname },
        } = this.props;
        let newLocation = `${pathname}?page=${page}`;
        if (pageSize) {
            newLocation += `&pageSize=${pageSize}`;
        }
        history.push(newLocation);
    }

    checkQueryString() {
        const {
            location: { search },
            parameter: { page, pageSize, parentNodeId },
        } = this.props;
        const parsedQueryString = parseQueryString<{ page?: string; pageSize?: string }>(search);
        const newPage = parsedQueryString.page ? convertToNumberIfString(parsedQueryString.page) : undefined;
        const newPageSize = parsedQueryString.pageSize
            ? convertToNumberIfString(parsedQueryString.pageSize)
            : undefined;
        if (newPage !== page || newPageSize !== pageSize) {
            this.props.updateLoadParameter({
                page: newPage,
                pageSize: newPageSize,
                parentNodeId,
            });
        }
    }

    render() {
        const {
            fields: { defaultPageSize },
            parameter,
            viewAll,
        } = this.props;
        const { pages, links } = this.state;

        if (!pages) {
            return null;
        }
        const page = parameter.page ?? 1;
        const pageSize = parameter.pageSize ?? defaultPageSize;
        const filerPages = pages.filter(
            // eslint-disable-next-line array-callback-return
            page1 =>
                page1.generalFields["eventEndDateTime"] &&
                !isPastDate(new Date(page1.generalFields["eventEndDateTime"])),
        );
        const sortedAndPagedPages = sortBy(filerPages, o => o.generalFields["eventStartDateTime"]);

        // const currentEvents = sortedAndPagedPages.length > 3 ? sortedAndPagedPages.slice(0, 2) : sortedAndPagedPages;
        const currentEvents = sortedAndPagedPages;

        if (!currentEvents || currentEvents.length === 0) {
            return null;
        }

        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.title}>{translate("Event & Webinars")}</Typography>
                <EventCarausel currentEvents={currentEvents} links={links} />
                {sortedAndPagedPages.length > 3 && (
                    <Link href="/Events" {...styles.viewAllLink}>
                        {translate("View All Events")}
                    </Link>
                )}
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(GAIANewsAndEventsListView)),
    definition: {
        group: "Common",
        displayName: "GAIA HomePage Events ",
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 3,
                fieldType: "Translatable",
                min: 1,
                max: 20,
            },
        ],
    },
};

export default widgetModule;
